import React, { useEffect, useState } from 'react'
import { Box, Typography, TextField, Button, CircularProgress } from '@mui/material'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import { Modal } from '../../../components/_EOS/Modal'
import { ScanIcon } from '../../../components/_WMS/_icons/ScanIcon'
import { OptionToggle } from '../../../components/_WMS/OptionToggle'
import { CameraScanButton } from '../../../components/_WMS/CameraScan'
import { StepProps } from '../../../components/_WMS/Stepper'
import { AsnReceptionState, AsnReceptionResult, ScanType, eStepAsnReception } from '../types'
import { searchAsnReception } from '../api'
import { useApi } from '../../../hooks/useApi'
import { useSnackbar } from '../../../hooks/useSnackbar'
import { useScanInput } from '../../../hooks/useScanInput'
import { SnackBar } from '../../../components/SnackBar/Snackbar'
import { Status } from '../../../hooks/useFetchState'
import { ApiResponse } from '../../../typings/ApiResponse'

interface ScanStepProps extends StepProps<AsnReceptionState> {}

export const ScanStep: React.FC<ScanStepProps> = ({
  goToStep,
  sharedState,
  setSharedState
}) => {
  const [noResultsModal, setNoResultsModal] = useState(false)
  const [noResultsValue, setNoResultsValue] = useState('')
  const { state, callApi } = useApi()
  const { isActive, isSuccess, message, openSnackBar } = useSnackbar()

  const isLoading = state.status === Status.Loading

  // Cablea lector fisico, tipeo manual y camara sobre la misma busqueda.
  // trimOnChange en false para no cambiar el comportamiento previo del input.
  const scan = useScanInput({
    onSearch: (value) => runSearch(value),
    disabled: isLoading,
    trimOnChange: false
  })

  useEffect(() => {
    scan.reset()
    if (sharedState.successMessage) {
      openSnackBar(sharedState.successMessage, true)
    }
    setSharedState((prev) => ({
      ...prev,
      scanType: 'ASN',
      scanValue: '',
      results: [],
      selectedResult: null,
      hideBackButton: true,
      successMessage: undefined
    }))
    return () => {
      setSharedState((prev) => ({ ...prev, hideBackButton: false }))
    }
  }, [])

  const handleScanTypeChange = (newType: ScanType) => {
    setSharedState((prev) => ({ ...prev, scanType: newType, scanValue: '' }))
    scan.reset()
  }

  /**
   * Busqueda parametrizada por valor. No lee del estado a proposito: asi puede
   * invocarse con un valor recien obtenido (camara) sin esperar el re-render.
   */
  const runSearch = (scanValue: string) => {
    if (!scanValue.trim() || isLoading) return
    callApi(searchAsnReception(sharedState.scanType, scanValue))
      .then((response: ApiResponse<AsnReceptionResult>) => {
        if (response.totalItems === 0) {
          setNoResultsValue(scanValue)
          setNoResultsModal(true)
          return
        }
        const single = response.totalItems === 1 ? response.items[0] : null
        setSharedState((prev) => ({ ...prev, scanValue, results: response.items, selectedResult: single }))
        if (response.totalItems === 1) {
          // Si hay un único resultado, saltar la pantalla de resultados e ir directo a la recepción
          goToStep(eStepAsnReception.ReceptionStep)
        } else {
          goToStep(eStepAsnReception.ResultsStep)
        }
      })
      .catch(() => openSnackBar('Error al buscar el registro', false))
  }

  const handleNoNumber = () => {
    goToStep(eStepAsnReception.UnknownIdStep)
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: 3, gap: 3, height: '100%' }}>
      <Typography variant='subtitle1' sx={{ color: '#333', fontWeight: 500 }}>
        Selecciona qué quieres escanear
      </Typography>

      <Box sx={{ width: '100%', maxWidth: 500 }}>
        <OptionToggle
          options={['ASN', 'LPN', 'SKU']}
          onChange={handleScanTypeChange}
          defaultValue='ASN'
        />
      </Box>

      <TextField
        fullWidth
        {...scan.fieldProps}
        variant='outlined'
        placeholder={`Escanea o escribe la ${sharedState.scanType}`}
        autoComplete='off'
        slotProps={{ input: { endAdornment: <ScanIcon width={40} height={52} /> } }}
        sx={{ maxWidth: 500, '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
      />

      <CameraScanButton
        {...scan.cameraProps}
        onError={(msg) => openSnackBar(msg, false)}
        disabled={isLoading}
        dialogTitle={`Escanear ${sharedState.scanType}`}
        helperText={`Centra el código de la ${sharedState.scanType} dentro del recuadro`}
      />

      <Button
        fullWidth
        variant='outlined'
        onClick={handleNoNumber}
        sx={{ maxWidth: 500, borderRadius: '16px', color: '#666', borderColor: '#bbb', textTransform: 'none' }}
      >
        No conozco el número de {sharedState.scanType}
      </Button>

      <Button
        fullWidth
        variant='contained'
        color='primary'
        onClick={scan.submit}
        disabled={!scan.value.trim() || isLoading}
        sx={{ maxWidth: 500, borderRadius: '10px', py: 1.5, textTransform: 'none' }}
        endIcon={isLoading ? <CircularProgress size={18} color='inherit' /> : undefined}
      >
        Siguiente
      </Button>

      <SnackBar message={message} open={isActive} status={isSuccess ? 'success' : 'error'} />

      <Modal
        open={noResultsModal}
        title='ASN sin LPNs asociadas'
        maxWidth='xs'
        onClose={() => setNoResultsModal(false)}
        successButton={{ action: () => setNoResultsModal(false), title: 'Aceptar' }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 56, height: 56, borderRadius: '50%', backgroundColor: '#fff8e1', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <WarningAmberIcon sx={{ fontSize: 32, color: '#f59e0b' }} />
          </Box>
          <Typography variant='body1' textAlign='center'>
            La ASN <b>{noResultsValue}</b> no tiene LPNs asociadas. Este módulo requiere una ASN con LPNs para poder controlar.
          </Typography>
        </Box>
      </Modal>
    </Box>
  )
}
